import { type ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

const SIZES = {
  /** A form: as wide as it needs, never taller than the screen. */
  md: "w-full max-h-[94vh] sm:w-[min(960px,90vw)] sm:max-h-[90vh]",
  /** A browsing surface: most of the screen, so there is room to look around. */
  lg: "h-[94vh] w-full sm:h-[90vh] sm:w-[90vw]",
  wlg: "h-[94vh] w-full sm:h-[90vh]",
} as const;

interface AppDialogProps {
  /** Names the dialog for screen readers; the visible title is free-form. */
  label: string;
  title: ReactNode;
  /** Header right — Cancel, Done, and the like. */
  actions: ReactNode;
  /** A second header row that stays put while the body scrolls, e.g. filters. */
  toolbar?: ReactNode;
  onDismiss: () => void;
  size?: keyof typeof SIZES;
  /** How wide the contents run inside the panel, matching the teacher pages' own measure. */
  measure?: string;
  bodyMeasure?: string | undefined;
  children: ReactNode;
}

/**
 * The teacher area's dialog: a white header bar over the app's sky, in a panel rather than the
 * whole viewport. Portalled, so an ancestor modal can neither clip it nor stack above it.
 */
export function AppDialog({
  label,
  title,
  actions,
  toolbar,
  onDismiss,
  size = "md",
  measure = "max-w-5xl",
  bodyMeasure,
  children,
}: AppDialogProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onDismiss]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={(event) => event.target === event.currentTarget && onDismiss()}
    >
      <div
        className={`flex flex-col overflow-hidden rounded-card bg-app-sky text-app-text shadow-xl ${SIZES[size]}`}
      >
        <header className="border-b border-gray-100 bg-white shadow-card-light">
          <div
            className={`mx-auto flex flex-wrap items-center gap-2 px-4 py-3 sm:flex-nowrap sm:gap-3 sm:px-6 ${measure}`}
          >
            <div className="flex min-w-0 flex-1 basis-full items-center gap-2 sm:basis-auto sm:gap-3">
              {title}
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
          </div>
          {toolbar && (
            <div className={`mx-auto border-t border-gray-100 px-4 py-2.5 sm:px-6 ${measure}`}>
              {toolbar}
            </div>
          )}
        </header>

        <div className="flex-1 overflow-y-auto">
          <div
            className={`mx-auto space-y-4 px-4 py-5 sm:space-y-5 sm:px-6 sm:py-8 ${bodyMeasure ?? measure}`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
